











































































































































































































import Vue from 'vue';
import { v4 as uuid } from 'uuid';
import { parse, differenceInDays, isSameDay } from 'date-fns';
import WbContentWrapper from '@/components/content_wrapper/index.vue';
import getOrder from './dependencies/get_order';
import { manageInstallment, InstallmentData } from './dependencies/manage_installment';
import WbOrder from '../../../order/index.vue';
import WbTextField from '@/components/text_field/index.vue';

interface Info {
  id: string;
  currency: string;
  priceInCents: number;
  installments: number;
  productName: string;
  studentId: string;
  studentName: string;
  projectId: string;
  projectName: string;
  agreementFileName: string;
  agreementLink: string;
  delinquencyStatus: string;
  agency: string;
  sentAt: Date;
  archivedAt: Date;
  status: string;
  loanBalanceInDollars: string;
  origin: 'ppm' | 'lnpl';
}

interface Installment {
  id: string;
  status: string;
  amountInCents: number;
  currency: string;
  dueDate: string;
  retryDate: Date | null;
  updatedAt: Date | null;
  bluesnapTransactionId: string | null;
  paidAt: string | null;
}

interface PaymentDetails {
  card?: {
    type: 'credit' | 'debit';
    brand: string;
    lastFourDigits: string;
    expirationMonth: string;
    expirationYear: string;
  };
  bank?: {
    accountType: string;
    lastFiveDigitsAccountNumber: string;
    lastFiveDigitsRoutingNumber: string;
  };
}

export default Vue.extend({

  components: {
    WbOrder,
    WbContentWrapper,
    WbTextField,
  },
  data() {
    return {
      order: {
        info: {} as Info,
        installments: [] as Installment[],
        paymentDetails: {} as PaymentDetails,
      },
      state: '',
      drawer: false,
      table: {
        itemsPerPage: 25,
        headers: [
          { text: 'ID', value: 'id', sortable: false },
          { text: 'Due Date', value: 'dueDate', sortable: false },
          { text: 'Amount', value: 'amount', sortable: false },
          { text: 'Retry Date', value: 'retryDate', sortable: false },
          { text: 'Paid Date', value: 'paidAt', sortable: false },
          { text: 'BlueSnap ID', value: 'bluesnapTransactionId', sortable: false },
          { text: 'Status', value: 'status', sortable: false },
          { text: 'Last Modified At', value: 'updatedAt', sortable: false },
        ],
      },
      errorSnackbar: false,
      errorText: '',
      formInstallments: [] as Installment[],
      newInstallment: {} as Partial<Installment>,
      isNewInstallmentFormVisible: false,
      installmentStatus: ['paid', 'terminated', 'failed', 'future'] as string[],
      newInstallmentError: '',
      loadingSubmitButton: false,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      try {
        this.state = 'loading';
        const data = await getOrder(this.$route.params.id);
        this.order = data;
        // keep it this way to avoid shallow copy of nested attr on JS
        // https://developer.mozilla.org/en-US/docs/Glossary/Deep_copy
        this.formInstallments = this.deepClone<Installment[]>(data.installments);
        this.state = 'loaded';
      } catch (error) {
        this.state = 'error';
      }
    },
    amountInCentsRules(amountInCents?: number) {
      return [
        () => this.isInputPositive(amountInCents),
        () => this.isInputInCents(amountInCents),
      ];
    },
    getQuantityPaid() {
      return this.order.installments.filter((i) => i.status === 'paid').length;
    },
    isPositive(n?: number) {
      return Boolean(n && n > 0);
    },
    isInCents(n?: number) {
      return Number.isInteger(n);
    },
    isInputPositive(n?: number) {
      return this.isPositive(n) || 'Must be greater than 0';
    },
    isInputInCents(n?: number) {
      return this.isInCents(n) || 'Value must be in CENTS';
    },
    isNewInstallmentButtonDisabled() {
      const { amountInCents, status, dueDate } = this.newInstallment;
      return !amountInCents
        || !status
        || !dueDate
        || !this.isPositive(amountInCents)
        || !this.isInCents(amountInCents);
    },
    isInstallmentScheduleChanged() {
      return this.formInstallments.length === this.order.installments.length
        && this.formInstallments.every((item, index) => (
          item.amountInCents === this.order.installments[index].amountInCents
          && item.id === this.order.installments[index].id
          && item.status === this.order.installments[index].status
          && isSameDay(new Date(item.dueDate), new Date(this.order.installments[index].dueDate))
        ));
    },
    renderList(id: string, status: string) {
      const inst = this.order.installments.find((x) => x.id === id);
      switch (inst?.status || status) {
      case 'paid':
        return ['paid'];
      case 'future':
      case 'failed':
      case 'terminated':
        return ['paid', 'terminated', 'failed', 'future'];
      case 'chargebacked':
        return ['chargebacked'];
      case 'refunded':
        return ['refunded'];
      default:
        return [];
      }
    },
    deepClone<T>(data: T): T {
      return JSON.parse(JSON.stringify(data)) as T;
    },
    isInputDisabled(id: string) {
      const inst = this.order.installments.find((x) => x.id === id);
      return [
        'chargebacked',
        'refunded',
        'canceled',
        'paid',
      ].includes(inst?.status || '');
    },
    async manageInstallmentsSubmit() {
      if (this.isInstallmentScheduleChanged()) {
        return;
      }

      if (!this.formInstallments.every((x) => this.isPositive(x.amountInCents))) {
        this.errorText = 'Installment Amount cannot be lower or equal to zero';
        this.errorSnackbar = true;
        return;
      }
      const total = this.getNewOrderBalance() - this.order.info.priceInCents;
      if (total !== 0) {
        this.errorText = `Total order balance is ${total > 0 ? 'HIGHER' : 'LOWER'} than original order amount`;
        this.errorSnackbar = true;
        return;
      }

      const toUpdate: InstallmentData[] = this.getDiffFromInstallments()
        .map((x) => ({
          id: x.id,
          dueDate: new Date(x.dueDate),
          currency: x.currency,
          amountInCents: x.amountInCents,
          status: x.status,
        }));
      this.loadingSubmitButton = !this.loadingSubmitButton;
      try {
        await manageInstallment(this.order.info.id, toUpdate);
        this.getData();
      } catch (err) {
        this.errorText = (err as any).message;
        this.errorSnackbar = true;
      } finally {
        this.loadingSubmitButton = !this.loadingSubmitButton;
      }
    },
    isFutureInstallmentValid(dueDate: string, status: string) {
      const newDueDate = parse(dueDate, 'yyyy-MM-dd', new Date());
      return status === 'future' && differenceInDays(newDueDate, new Date()) >= 0;
    },
    getNewOrderBalance() {
      const newBalance = this.formInstallments.reduce(
        (prev, curr) => (['paid', 'future', 'failed'].includes(curr.status) ? curr.amountInCents + prev : prev), 0,
      );
      return newBalance;
    },
    removeInstallment(id: string) {
      const index = this.formInstallments.findIndex((x) => x.id === id);
      this.formInstallments.splice(index, 1);
    },
    newInstallmentSubmit() {
      if (
        this.newInstallment.status === 'future'
        && !this.isFutureInstallmentValid(this.newInstallment.dueDate!, this.newInstallment.status!)
      ) {
        this.errorText = 'Future Installment cannot have a date in the past';
        this.errorSnackbar = true;
        return;
      }

      this.formInstallments.push({
        id: uuid(),
        status: this.newInstallment.status!,
        currency: this.order.info.currency,
        amountInCents: this.newInstallment.amountInCents!,
        dueDate: this.newInstallment.dueDate!,
        updatedAt: null,
        bluesnapTransactionId: null,
        paidAt: null,
        retryDate: null,
      });
      this.newInstallment = {};
    },
    showInstallmentForm() {
      this.isNewInstallmentFormVisible = !this.isNewInstallmentFormVisible;
    },
    isNewInstallment(id: string) {
      return !this.order.installments.find((x) => x.id === id);
    },
    resetFormInstallments() {
      this.formInstallments = this.deepClone<Installment[]>(this.order.installments);
    },
    getDiffFromInstallments() {
      const currentIds = this.order.installments.map((x) => x.id);
      const newInstallments = this.formInstallments.filter((e) => !currentIds.includes(e.id));

      const updatedInstallments = this.formInstallments.filter((item) => {
        const inst = this.order.installments.find((x) => x.id === item.id);
        return inst
          && (item.amountInCents !== inst.amountInCents
          || item.status !== inst.status
          || !isSameDay(new Date(item.dueDate), new Date(inst.dueDate)));
      });

      return [...updatedInstallments, ...newInstallments];
    },
  },
});
